import * as Sentry from '@sentry/react';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
    LOGIN_REQUESTED,
    PAYMENT_REQUESTED,
    SIGNUP_REQUESTED,
    ROLE_SWITCH_REQUESTED,
    FB_LOGIN_REQUESTED,
    PERMISSIONS,
    PARTNER_SIGNUP_REQUESTED,
    FORGOT_PASSWORD_REQUESTED,
    RESET_PASSWORD_REQUESTED,
    AVAILABLE_PACKAGES,
    GET_PARTNER_LICENSE,
    paymentEmailBody,
    EMAIL_TO,
    GET_PACKAGE_PRICING_REQUEST,
    GET_PARTNER_LIST_REQUEST,
    GET_PARTNER_TOKEN_REQUEST,
    GET_REFRESH_TOKEN_REQUEST,
    PERMISSIONS_REQUESTED,
} from './constants';
import {
    loginSucceeded,
    loginFailed,
    paymentSucceeded,
    paymentFailed,
    updateUserRequested,
    signUpSucceeded,
    signUpFailed,
    roleSwitchSucceeded,
    roleSwitchFailed,
    partnerSignupFailed,
    partnerSignupSuccessful,
    forgotPasswordSucceeded,
    forgotPasswordFailed,
    resetPasswordSucceeded,
    resetPasswordFailed,
    updatePartnerLicense,
    getPackagePricingSucceeded,
    getPackagePricingFailed,
    getPartnerListSucceeded,
    getPartnerListFailed,
    getPartnerTokenSucceeded,
    getPartnerTokenFailed,
    getRefreshTokenSucceeded,
    getRefreshTokenFailed,
    gotUserPermissions,
} from './actions';
import {
    login,
    fbLogin,
    processPayment,
    register,
    getPricing,
    getPartners,
    updateUserToPaidContributor,
    switchUsersRole,
    createPartner,
    forgotPassword,
    resetPassword,
    getPartnerLicense,
    sendEmail,
    changeUserRole,
    getUserPermissions,
    getPartnerProfile,
    getPartnerToken,
    getUserToken,
} from '../../api/';
import { parseClientError } from '../../api/utils';
import {
    getUserCreds,
    getTokenCreds,
    getActivePackge,
    getActivePackgePrice,
    getActivePackageKey,
    getActivePackageId,
    getSubscription,
    getRequestedRoleId,
    getPartnerSignupDetails,
    getForgotPasswordEmail,
    getVerificationCode,
    getLimitValue,
    getSkipValue,
} from './selectors';
import { setAuthToken } from '../../api/utils';
import { getUser } from '../auth/selectors';
import { ROUTE_DASHBOARD } from '../../navigation/constants';

// triggers when user `LOGIN_REQUESTED` action is dispatched
function* performLogin() {
    const creds = yield select(getUserCreds);
    try {
        const user = yield call(login, creds);
        const allowAccess = user.rolePermissions.permissions.filter((element) =>
            PERMISSIONS.includes(element.key),
        );

        if (allowAccess.length) {
            setAuthToken(user.token);
            const isPartner = user.rolePermissions.key == 'partner';

            if (isPartner) {
                const { data } = yield call(getPartnerProfile);
                user.partner = data;
                user.isPartner = true;
            }
            yield put(loginSucceeded(user));
        } else {
            yield put(loginFailed('Access Denied'));
        }
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(loginFailed(message));
    }
}

function* performFbLogin() {
    const creds = yield select(getUserCreds);

    try {
        const user = yield call(fbLogin, creds);

        setAuthToken(user.token);
        const isPartner =
            user.rolePermissions && user.rolePermissions.key == 'partner';

        if (isPartner) {
            const { data } = yield call(getPartnerProfile);
            user.partner = data;
            user.isPartner = true;
        }
        yield put(loginSucceeded(user));
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(loginFailed(message));
    }
}

function* performPayment() {
    const tokenCreds = yield select(getTokenCreds);
    const packageName = yield select(getActivePackge);
    const packagePrice = yield select(getActivePackgePrice);
    const packageKey = yield select(getActivePackageKey);
    const packageId = yield select(getActivePackageId);
    const loggedInUser = yield select(getUser);

    const paymentCreds = {
        email: loggedInUser.email,
        name: loggedInUser.fullName
            ? loggedInUser.fullName
            : loggedInUser.username,
        source: tokenCreds.id,
        priceId: packageId,
    };
    // params required for the trantision of user to paid contributor
    const transitionBody = {
        package: packageKey,
        role: loggedInUser.role,
        partnerId: loggedInUser.partnerId,
        id: loggedInUser.id,
    };

    try {
        const paymentResponse = yield call(processPayment, paymentCreds);

        if (paymentResponse.status == 200) {
            const updatedUser = yield call(
                updateUserToPaidContributor,
                transitionBody,
            );

            //Send Email to Super Admins when someone makes Payment
            const body = paymentEmailBody(
                loggedInUser.username,
                loggedInUser.email,
                packageName,
                packagePrice,
            );
            const emailSubject = `Partner SignUp`;
            yield call(sendEmail, {
                to: EMAIL_TO,
                body,
                subject: emailSubject,
            });

            yield put(paymentSucceeded(paymentResponse));
            yield put(updateUserRequested(updatedUser));
        } else {
            let message = paymentResponse.message
                ? paymentResponse.message
                : '';

            message = typeof message == 'string' ? message : '';
            yield put(paymentFailed(message));
        }
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        let message = error && error.message ? error.message : error;
        message = typeof message == 'string' ? message : '';
        yield put(paymentFailed(message));
    }
}

function* performSignUp() {
    const creds = yield select(getUserCreds);
    const subscription = yield select(getSubscription);
    try {
        const user = yield call(register, {
            ...creds,
            fullName: creds.username,
            campaign: subscription,
        });

        setAuthToken(user.token);
        yield put(signUpSucceeded(user));
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(signUpFailed(message));
    }
}

function* switchUserRole() {
    try {
        const roleToSwitch = yield select(getRequestedRoleId);
        const switchRolePayload = yield call(switchUsersRole, roleToSwitch);
        const isPartner =
            switchRolePayload.rolePermissions &&
            switchRolePayload.rolePermissions.key == 'partner';

        if (isPartner) {
            const { data } = yield call(getPartnerProfile);
            switchRolePayload.partner = data;
            switchRolePayload.isPartner = true;
        }

        if (switchRolePayload) {
            yield put(roleSwitchSucceeded(switchRolePayload));
            window.location.href = `${window.location.origin}${ROUTE_DASHBOARD}`;
        } else {
            yield put(roleSwitchFailed(switchRolePayload));
        }
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(roleSwitchFailed(message));
    }
}

function* PartnerSignupProcess() {
    const partnerDetails = yield select(getPartnerSignupDetails);
    let activePackage = yield select(getActivePackageKey);
    const partnerTypeKey = AVAILABLE_PACKAGES[activePackage].defaultRoleKey;
    const loggedInUser = yield select(getUser);
    try {
        if (typeof partnerDetails.banner != 'string')
            delete partnerDetails.banner;
        yield call(createPartner, {
            ...partnerDetails,
            partnerTypeKey,
        });
        // This api revokes the user's 201 role.
        // Needs to be handled more elegantly later.
        yield call(changeUserRole, { userId: loggedInUser.id });
        const permissions = yield call(getUserPermissions);
        yield put(partnerSignupSuccessful(permissions));
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(partnerSignupFailed(message));
    }
}

function* getPersmissions() {
    try {
        const permissions = yield call(getUserPermissions);
        yield put(partnerSignupSuccessful(permissions));
        yield put(gotUserPermissions());
    } catch (e) {
        Sentry.captureException(e);
        yield put(gotUserPermissions());
        console.log(e);
    }
}

function* sendForgotPasswordEmail() {
    const email = yield select(getForgotPasswordEmail);
    try {
        yield call(forgotPassword, email);
        yield put(forgotPasswordSucceeded(true));
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(forgotPasswordFailed(message));
    }
}

function* performResetPassword(action) {
    const code = yield select(getVerificationCode);
    const { password } = action;
    try {
        yield call(resetPassword, { token: code, password });
        yield put(resetPasswordSucceeded(true));
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(resetPasswordFailed(message));
    }
}

function* fetchPartnerLicense() {
    const partnerLicense = yield call(getPartnerLicense);
    yield put(updatePartnerLicense(partnerLicense));
}

function* fetchPricing() {
    try {
        const pricing = yield call(getPricing);
        yield put(getPackagePricingSucceeded(pricing));
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(getPackagePricingFailed(message));
    }
}

function* fetchPartners() {
    try {
        const skip = yield select(getSkipValue);
        const limit = yield select(getLimitValue);
        const partners = yield call(getPartners, { skip, limit });
        yield put(getPartnerListSucceeded(partners));
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(getPartnerListFailed(message));
    }
}

function* fetchPartnerToken(action) {
    try {
        const { partnerId } = action;
        const partner = yield call(getPartnerToken, { partnerId });
        setAuthToken(partner.token);

        const { data } = yield call(getPartnerProfile);
        partner.partner = data;
        partner.isPartner = true;

        yield put(getPartnerTokenSucceeded(partner));
        window.location.href = `${window.location.origin}${ROUTE_DASHBOARD}`;
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(getPartnerTokenFailed(message));
    }
}

function* fetchUserToken() {
    try {
        const user = yield call(getUserToken);
        setAuthToken(user.token);
        const isPartner = user.rolePermissions.key == 'partner';
        if (isPartner) {
            const { data } = yield call(getPartnerProfile);
            user.partner = data;
            user.isPartner = true;
        }

        yield put(getRefreshTokenSucceeded(user));
        window.location.href = `${window.location.origin}${ROUTE_DASHBOARD}`;
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(getRefreshTokenFailed(message));
    }
}

export default function*() {
    yield all([
        yield takeLatest(LOGIN_REQUESTED, performLogin),
        yield takeLatest(PAYMENT_REQUESTED, performPayment),
        yield takeLatest(SIGNUP_REQUESTED, performSignUp),
        yield takeLatest(ROLE_SWITCH_REQUESTED, switchUserRole),
        yield takeLatest(FB_LOGIN_REQUESTED, performFbLogin),
        yield takeLatest(PARTNER_SIGNUP_REQUESTED, PartnerSignupProcess),
        yield takeLatest(FORGOT_PASSWORD_REQUESTED, sendForgotPasswordEmail),
        yield takeLatest(RESET_PASSWORD_REQUESTED, performResetPassword),
        yield takeLatest(GET_PARTNER_LICENSE, fetchPartnerLicense),
        yield takeLatest(GET_PACKAGE_PRICING_REQUEST, fetchPricing),
        yield takeLatest(GET_PARTNER_LIST_REQUEST, fetchPartners),
        yield takeLatest(GET_PARTNER_TOKEN_REQUEST, fetchPartnerToken),
        yield takeLatest(GET_REFRESH_TOKEN_REQUEST, fetchUserToken),
        yield takeLatest(PERMISSIONS_REQUESTED, getPersmissions),
    ]);
}
